import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { procedureKeys } from '@/features/procedures/api'
import type { ListMeta, Query } from '@/lib/api'
import { useApi } from '@/lib/api-context'
import { useIdempotencyKey } from '@/lib/use-idempotency-key'

/**
 * Pacientes (docs/api/pacientes.md; regras em docs/areas/pacientes.md, RN-PAC-01 a 30). Os tipos
 * são os do contrato da função `patients`; as telas só falam com a API por estes hooks (T25).
 */

export type DocType = 'cpf' | 'cnpj'
/**
 * O público da pessoa (RN-PAC-36, P-DOC-30): paciente, aluno, funcionário ou terceiro. O mesmo
 * vocabulário do público do documento e da clínica (`clinics.audiences`).
 */
export type Audience = 'patient' | 'student' | 'employee' | 'third_party'
export type PatientStatus = 'active' | 'archived' | 'merged'
export type PendingLevel = 'critical' | 'attention' | 'waiting'
export type MaritalStatus =
  'single' | 'married' | 'domestic_partnership' | 'separated' | 'divorced' | 'widowed'
export type Gender = 'female' | 'male' | 'other' | 'not_provided'
export type ImageConsent = 'yes' | 'no' | 'not_provided'
export type ReferralChannel =
  | 'referral'
  | 'social_media'
  | 'instagram'
  | 'google'
  | 'ad'
  | 'website'
  | 'storefront'
  | 'event'
  | 'other'
export type ForeignDocumentType = 'passport' | 'rne' | 'crnm' | 'other'

export interface Address {
  /** 8 dígitos; texto livre de até 20 quando `country` não é `BR` (P-PAC-13). */
  postalCode: string
  street: string
  number: string
  complement: string | null
  district: string
  city: string
  /** Duas letras; texto livre de até 60 quando `country` não é `BR`. */
  state: string
  /** ISO 3166-1 alfa-2; padrão `BR`. */
  country: string
}

export interface ForeignDocument {
  type: ForeignDocumentType
  number: string
  country: string
}

export interface Guardian {
  name: string
  docType: 'cpf'
  docNumber: string
  /** E.164 na resposta. */
  whatsapp: string
}

export interface ReferralSource {
  channel: ReferralChannel
  detail: string | null
}

export type CompletenessField =
  'email' | 'birthDate' | 'idDocument' | 'address' | 'maritalStatus' | 'guardian'

/** O medidor de cadastro completo (RN-PAC-09). */
export interface Completeness {
  percent: number
  isReadyForContract: boolean
  missing: { field: CompletenessField; label: string; consequence: string }[]
}

export type PendingItemType =
  | 'procedure_done_unsigned'
  | 'link_expired'
  | 'not_signed'
  | 'awaiting_clinic_signature'
  | 'signature_overdue'
  | 'document_generated_not_sent'
  | 'anamnesis_not_completed'
  | 'treatment_plan_without_product'
  | 'treatment_plan_lot_pending'
  | 'treatment_plan_photos_incomplete'
  | 'awaiting_signature'
  | 'document_generated'

/** Um item de pendência (RN-PAC-10, vocabulário de RT-24). */
export interface PendingItem {
  type: PendingItemType
  level: PendingLevel
  /** Curto, para a etiqueta: "2 documentos". */
  label: string
  /** Por extenso, para a ficha: "2 documentos aguardando assinatura há 18 dias". */
  text: string
  target: { type: 'appointment' | 'anamnesis' | 'treatment_plan'; id: string }
  since: string
}

export type HighlightField = 'name' | 'email' | 'docNumber' | 'rg' | 'whatsapp' | 'foreignDocument'

/** Linha da lista (`GET /patients`). */
export interface PatientListItem {
  id: string
  name: string
  email: string | null
  whatsapp: string | null
  docType: DocType | null
  docNumber: string | null
  rg: string | null
  status: PatientStatus
  audience: Audience
  pendingItem: { level: PendingLevel | null; label: string | null; total: number }
  completeness: { percent: number; isReadyForContract: boolean }
  /** Quantos cadastros no grupo, incluindo este; 0 ou 1 quer dizer nenhum duplicado. */
  duplicates: { total: number }
  highlights: { field: HighlightField; snippet: string }[]
  sourceClinic: { id: string; name: string } | null
}

export type DuplicateMatchKey = 'docNumber' | 'foreignDocument' | 'whatsapp'

/** A ficha (`GET /patients/:id`). */
export interface Patient {
  id: string
  status: PatientStatus
  /** Não muda depois do primeiro documento (RN-PAC-37). */
  audience: Audience
  name: string
  birthDate: string | null
  age: number | null
  isMinor: boolean
  docType: DocType | null
  docNumber: string | null
  foreignDocument: ForeignDocument | null
  isForeign: boolean
  rg: string | null
  gender: Gender
  maritalStatus: MaritalStatus | null
  occupation: string | null
  email: string | null
  /** Nulo só em cadastro importado sem WhatsApp ou anonimizado. */
  whatsapp: string | null
  address: Address | null
  guardian: Guardian | null
  imageConsent: ImageConsent
  instagram: string | null
  notes: string | null
  referralSource: ReferralSource | null
  patientSince: string
  /** Quando a própria paciente se cadastrou pelo link da clínica (RN-PAC-34); nulo no cadastro da clínica. */
  selfRegisteredAt: string | null
  createdAt: string
  updatedAt: string
  archivedAt: string | null
  archivedBy: string | null
  mergedIntoId: string | null
  anonymizedAt: string | null
  anonymizedBy: string | null
  sourceClinic: { id: string; name: string } | null
  /** Cadastro de clínica irmã da rede: só leitura (RT-06, RN-PAC-23). */
  isReadOnly: boolean
  completeness: Completeness
  pendingItems: { total: number; level: PendingLevel | null; items: PendingItem[] }
  duplicates: {
    total: number
    patients: { id: string; name: string; matchKey: DuplicateMatchKey }[]
  }
  counts: {
    anamneses: number
    documents: number
    treatmentPlans: { open: number; closed: number }
  }
}

/**
 * O corpo de `POST /patients` e de `PUT /patients/:id`. No `PUT` vai só o que muda: ausente
 * mantém, `null` limpa (RN-PAC-27). `address` e `guardian` vão inteiros.
 */
export interface PatientInput {
  /** A confirmação do preenchimento rápido (RN-PAC-58): o e-mail confirmado entra com ela. */
  identity?: { prefillId: string }
  /** Padrão `patient`; só um público que a clínica contratou (RN-PAC-36). */
  audience?: Audience
  name?: string
  /**
   * No Brasil, DDD e número como digitados (a API completa com o DDI padrão); de outro país, o
   * número inteiro começando por "+". A API guarda em E.164.
   */
  whatsapp?: string
  dialCode?: string
  email?: string | null
  birthDate?: string | null
  /** A API o tira do tamanho do número; `cnpj` só fora do público `patient` (RN-PAC-38). */
  docType?: DocType
  docNumber?: string | null
  foreignDocument?: ForeignDocument | null
  rg?: string | null
  gender?: Gender
  maritalStatus?: MaritalStatus | null
  occupation?: string | null
  address?: Address | null
  guardian?: Guardian | null
  imageConsent?: ImageConsent
  instagram?: string | null
  notes?: string | null
  referralSource?: ReferralSource | null
}

/** O `existing` do 409 de CPF repetido (RN-PAC-03). */
export interface ExistingPatient {
  id: string
  name: string
  archivedAt: string | null
}

export type PatientFilter = 'pending' | 'incomplete' | 'all' | 'archived'
export type PatientSort = 'urgency' | 'name' | '-name' | 'patientSince' | '-patientSince'
export type PatientPerPage = 10 | 25 | 50

export interface PatientListParams {
  filter: PatientFilter
  search?: string
  sort?: PatientSort
  page?: number
  perPage?: PatientPerPage
  practitionerId?: string
  procedureId?: string
  /** Período de atendimentos, `aaaa-mm-dd` (P-PAC-16). */
  from?: string
  to?: string
  /** O público da pessoa (RN-PAC-39); ausente, todos. */
  audience?: Audience
}

export interface PatientListMeta extends ListMeta {
  /** Sobre a busca, os filtros adicionais e o público, nunca sobre o segmento (RN-PAC-12). */
  counts: { pending: number; incomplete: number; all: number }
  /**
   * Os públicos da clínica e os que têm cadastro, com os ativos de cada um, nunca sobre o segmento
   * nem o público escolhido (RN-PAC-39). Com mais de um, a lista mostra o segmento de público.
   */
  audiences: { audience: Audience; count: number }[]
}

/** Um cadastro dentro de um grupo de duplicados (`GET /patients/duplicates`). */
export interface DuplicatePatient {
  id: string
  name: string
  email: string | null
  whatsapp: string | null
  patientSince: string
  completeness: { percent: number; isReadyForContract: boolean }
  counts: {
    anamneses: number
    signedDocumentCount: number
    documents: number
    treatmentPlans: number
  }
}

export interface DuplicateGroup {
  matchKey: DuplicateMatchKey
  /** Já formatado para exibição: "529.982.247-25". */
  value: string
  suggestedPrimaryId: string
  patients: DuplicatePatient[]
}

export interface MergeInput {
  primaryId: string
  absorbedIds: string[]
}

export interface MergedPatient extends Patient {
  merge: {
    absorbed: string[]
    moved: { anamneses: number; appointments: number; documents: number; treatmentPlans: number }
    filledFields: string[]
  }
}

/** `GET /patients/postal-code/:postalCode`. CEP geral de cidade não tem rua nem bairro. */
export interface PostalCodeAddress {
  postalCode: string
  street: string | null
  district: string | null
  city: string
  state: string
}

/** O `awaitingSignature` do 409 ao arquivar com envio pendente (RN-PAC-18). */
export interface AwaitingSignature {
  appointmentId: string
  documentCount: number
}

export const patientKeys = {
  all: ['patients'] as const,
  lists: ['patients', 'list'] as const,
  list: (params: object) => ['patients', 'list', params] as const,
  detail: (id: string) => ['patients', 'detail', id] as const,
  duplicates: (patientId: string) => ['patients', 'duplicates', patientId] as const,
}

function listQuery(params: PatientListParams): Query {
  return {
    filter: params.filter,
    search: params.search,
    sort: params.sort,
    practitionerId: params.practitionerId,
    procedureId: params.procedureId,
    from: params.from,
    to: params.to,
    audience: params.audience,
  }
}

export function usePatients(params: PatientListParams) {
  const api = useApi()
  return useQuery({
    queryKey: patientKeys.list(params),
    queryFn: () =>
      api.list<PatientListItem, PatientListMeta>('/patients', {
        ...listQuery(params),
        page: params.page ?? 1,
        perPage: params.perPage ?? 10,
      }),
    placeholderData: (previous) => previous,
  })
}

export function usePatient(id: string | undefined) {
  const api = useApi()
  return useQuery({
    queryKey: patientKeys.detail(id ?? ''),
    queryFn: () => api.get<Patient>(`/patients/${id}`),
    enabled: Boolean(id),
  })
}

/** Cria com o mínimo (nome e WhatsApp, RN-PAC-01). CPF repetido volta 409 com `existing`. */
export function useCreatePatient() {
  const api = useApi()
  const queryClient = useQueryClient()
  const idempotency = useIdempotencyKey()
  return useMutation({
    mutationFn: (input: PatientInput) =>
      api.post<Patient>('/patients', input, { idempotencyKey: idempotency.keyFor(input) }),
    onSuccess: (patient) => {
      idempotency.reset()
      queryClient.setQueryData(patientKeys.detail(patient.id), patient)
      return queryClient.invalidateQueries({ queryKey: patientKeys.lists })
    },
  })
}

/** Edita só os campos enviados (RN-PAC-27); a resposta traz o medidor recalculado. */
export function useUpdatePatient(id: string) {
  const api = useApi()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (input: PatientInput) => api.put<Patient>(`/patients/${id}`, input),
    onSuccess: (patient) => {
      queryClient.setQueryData(patientKeys.detail(id), patient)
      return Promise.all([
        queryClient.invalidateQueries({ queryKey: patientKeys.lists }),
        queryClient.invalidateQueries({ queryKey: patientKeys.duplicates(id) }),
      ])
    },
  })
}

/** Arquivar e restaurar (gestora e root, RN-PAC-18). */
export function usePatientAction() {
  const api = useApi()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ id, action }: { id: string; action: 'archive' | 'restore' }) =>
      api.post<Patient>(`/patients/${id}/${action}`),
    onSuccess: (patient) => {
      queryClient.setQueryData(patientKeys.detail(patient.id), patient)
      return queryClient.invalidateQueries({ queryKey: patientKeys.lists })
    },
  })
}

/** Os grupos de duplicados que contêm este cadastro (RN-PAC-16). */
export function usePatientDuplicates(patientId: string | undefined) {
  const api = useApi()
  return useQuery({
    queryKey: patientKeys.duplicates(patientId ?? ''),
    queryFn: () =>
      api.list<DuplicateGroup>('/patients/duplicates', { patientId, page: 1, perPage: 50 }),
    enabled: Boolean(patientId),
  })
}

/** Unifica de 2 a 5 cadastros num principal (RN-PAC-17). Irreversível. */
export function useMergePatients() {
  const api = useApi()
  const queryClient = useQueryClient()
  const idempotency = useIdempotencyKey()
  return useMutation({
    mutationFn: (input: MergeInput) =>
      api.post<MergedPatient>('/patients/merge', input, {
        idempotencyKey: idempotency.keyFor(input),
      }),
    onSuccess: () => {
      idempotency.reset()
      // Os absorvidos somem, o principal muda e os grupos se desfazem: tudo de pacientes é relido.
      return queryClient.invalidateQueries({ queryKey: patientKeys.all })
    },
  })
}

/** Consulta de CEP pela API (RN-PAC-08): só preenche; o endereço nunca depende dela. */
export function useLookupPostalCode() {
  const api = useApi()
  return useMutation({
    mutationFn: (postalCode: string) =>
      api.get<PostalCodeAddress>(`/patients/postal-code/${postalCode}`),
  })
}

/** Uma opção dos filtros adicionais da lista (prestador, procedimento). */
export interface FilterOption {
  id: string
  name: string
}

/**
 * Os procedimentos da clínica para o filtro da lista (P-PAC-16). Debaixo de `procedureKeys.all`:
 * criar ou arquivar um procedimento refaz também esta escolha.
 */
export function useProcedureFilterOptions(enabled: boolean) {
  const api = useApi()
  return useQuery({
    queryKey: [...procedureKeys.all, 'filter-options'] as const,
    queryFn: async () => {
      // A API pagina de 50 em 50: o select precisa de todos, então lê página a página.
      const options: FilterOption[] = []
      for (let page = 1; ; page++) {
        const result = await api.list<FilterOption>('/admin/procedures', {
          filter: 'all',
          page,
          perPage: 50,
        })
        options.push(...result.data)
        if (page >= result.meta.totalPages) return options
      }
    },
    enabled,
  })
}

/** Exporta o CSV com os mesmos parâmetros da lista, sem paginação (gestora e root, RN-PAC-22). */
export function useExportPatients() {
  const api = useApi()
  return useMutation({
    mutationFn: (params: PatientListParams) => api.download('/patients/export', listQuery(params)),
  })
}

// ── Link de cadastro (RN-PAC-31, RN-PAC-32; T37) ──────────────────────────────────────────────

/** O link público de cadastro da clínica ativa, como `GET /patients/registration-link` devolve. */
export interface RegistrationLink {
  url: string
  token: string
  issuedAt: string
  clinic: {
    id: string
    name: string
    city: string | null
    state: string | null
    /** URL assinada de 15 minutos, ou nulo sem logo. */
    logoUrl: string | null
  }
}

export const registrationLinkKey = ['patients', 'registration-link'] as const

export function useRegistrationLink() {
  const api = useApi()
  return useQuery({
    queryKey: registrationLinkKey,
    queryFn: () => api.get<RegistrationLink>('/patients/registration-link'),
  })
}

/** Gestora e root: troca o token; o link anterior deixa de abrir na hora (RN-PAC-32). */
export function useRegenerateRegistrationLink() {
  const api = useApi()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: () => api.post<RegistrationLink>('/patients/registration-link/regenerate', {}),
    onSuccess: (link) => queryClient.setQueryData(registrationLinkKey, link),
  })
}
